import {
  DONT_HAVE_THIS_COLOR,
  notAnsweredColor,
  notAnsweredStyling,
} from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/CategoryChartsConfigs/categoryChartConfigsUtils'
import type { StatisticsSummary } from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/DataHubSummaryChart'
import { SummaryChartDataTypes } from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/PeriodChartsConfigs/usePeriodSummaryChartConfigs'
import { getVisualizationColorsMaxContrast } from '@app/src/utils/statisticsUtils'
import { darken, useTheme } from '@mui/material'

import type { EChartsOption } from 'echarts'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import { PieChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TitleComponent, TooltipComponent } from 'echarts/components'
import * as echarts from 'echarts/core'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { ResultForGroup, StatisticsForQuestionPerGroup } from '../../../DataHubScene'
import type { UserSelection } from '../DataInsightsModalContext'
import { useDataInsightsModal } from '../DataInsightsModalContext'
import type { EchartClickItemData } from './OptionMultiGroupChart'

interface OptionBarProps {
  statistics: StatisticsForQuestionPerGroup
  statisticsSummary: StatisticsSummary
  height: string
  echartsRef: React.MutableRefObject<ReactEChartsCore | null>
}

const basePiechartItem = (count: number, color: string) => {
  return {
    value: count,
    emphasis: { itemStyle: { color: darken(color, 0.2) }, scale: false },
    itemStyle: { color: color },
    label: {
      show: count > 0,
      formatter: `{marker|●} {b} ({c})`,
      rich: {
        marker: {
          color: color,
          fontSize: 28,
        },
      },
    },
  }
}
export const OptionSingleGroupPieChart: React.FC<OptionBarProps> = ({
  statistics,
  statisticsSummary,
  height,
  echartsRef,
}) => {
  const { formatMessage } = useIntl()
  const { palette } = useTheme()

  const { openDataInsightsModal } = useDataInsightsModal()

  const resultsForGroup: ResultForGroup = statistics.resultForGroups[0]
  const events = useMemo(
    () => ({
      click: ({ data }: { data: EchartClickItemData }) => {
        const userSelection: UserSelection = {
          answer: data.answersSelected,
          groupName: data.groupName,
          groupId: data.groupId,
          summaryType: data.summaryType ? [data.summaryType] : [],
        }
        openDataInsightsModal(statistics, userSelection)
      },
    }),
    [openDataInsightsModal, resultsForGroup.groupName, statistics],
  )

  echarts.use([TitleComponent, TooltipComponent, GridComponent, PieChart, LegendComponent])
  const options: EChartsOption = useMemo(() => {
    const maxNumberOfOptionsToShow = 10
    const optionStatisticsView =
      resultsForGroup?.optionStatisticsViews?.find(op => op.maxItemCount === maxNumberOfOptionsToShow) ??
      resultsForGroup.optionStatisticsViews?.[0]
    const itemsInView = optionStatisticsView?.items ?? []
    const colors =
      itemsInView.length === 1 ? [palette.visualization[1]] : getVisualizationColorsMaxContrast(itemsInView.length)

    const skippedItem = {
      name: formatMessage({ id: 'statistics.dataInsights.dontHaveThis' }),
      summaryType: SummaryChartDataTypes.CompaniesAnsweredDontHaveThis,
      ...basePiechartItem(statisticsSummary.resultForGroups[0].companiesAnsweredDontHaveThis, DONT_HAVE_THIS_COLOR),
    }

    const noResponseItem = {
      name: formatMessage({ id: 'statistics.dataInsights.noResponse' }),
      summaryType: SummaryChartDataTypes.CompaniesNotAnswered,
      value: statisticsSummary.resultForGroups[0].companiesNotAnswered,
      emphasis: { scale: false },
      ...notAnsweredStyling(palette),
      label: {
        show: statisticsSummary.resultForGroups[0].companiesNotAnswered > 0,
        formatter: `{stripedMarker|▨} {b} ({c})`,
        rich: {
          stripedMarker: {
            color: notAnsweredColor(palette),
            fontSize: 28,
          },
        },
      },
    }

    const optionData =
      itemsInView.map((op, index) => ({
        name: op.name,
        answersSelected: op.isOther ? (optionStatisticsView?.valuesInOther ?? []) : [op.name],
        ...basePiechartItem(op.count, colors[index]),
      })) ?? []

    // Only include items that have data
    const data = [
      ...optionData,
      ...(statisticsSummary.resultForGroups[0].companiesAnsweredDontHaveThis > 0 ? [skippedItem] : []),
      ...(statisticsSummary.resultForGroups[0].companiesNotAnswered > 0 ? [noResponseItem] : []),
    ]

    return {
      tooltip: {
        trigger: 'item',
        confine: true,
      },
      color: colors,
      series: [
        {
          type: 'pie',
          data: data,
          itemStyle: {
            borderColor: '#ffffff',
            borderWidth: 3,
          },
        },
      ],
    }
  }, [formatMessage, resultsForGroup.optionStatisticsViews, statisticsSummary])

  return (
    <ReactEChartsCore
      onEvents={events}
      option={options}
      echarts={echarts}
      style={{ minWidth: '100%', height }}
      ref={echartsRef}
    />
  )
}
